import type { QueryClient } from "@tanstack/react-query";
import { useAuthV2 } from "@/lib/authv2/store";

type Mode = "branch" | "office" | "remote";
export type CheckinAttempt = { owner: string | null; epoch: number; mode: Mode };
type State = {
  pending: CheckinAttempt | null;
  checkingOut: boolean;
  completed: (CheckinAttempt & { until: number }) | null;
};

const attempts = new WeakMap<QueryClient, State>();
let identityEpoch = 0;
let knownOwner: string | null = null;

// A callback from the old session must not submit after the user signs out or switches accounts.
function observeIdentity(): void {
  const owner = useAuthV2.getState().casdoorSub;
  if (owner !== knownOwner) {
    knownOwner = owner;
    identityEpoch++;
  }
}

// Some isolated screen tests mock only getState; the synchronous observer also
// runs on every claim and response, so it remains safe without subscribe.
if (typeof useAuthV2.subscribe === "function") useAuthV2.subscribe(observeIdentity);

function attemptState(qc: QueryClient): State {
  let state = attempts.get(qc);
  if (!state) {
    state = { pending: null, checkingOut: false, completed: null };
    attempts.set(qc, state);
  }
  return state;
}

export function beginCheckinAttempt(qc: QueryClient, mode: Mode): CheckinAttempt | null {
  observeIdentity();
  const owner = useAuthV2.getState().casdoorSub;
  const state = attemptState(qc);
  if (state.checkingOut || (state.pending && isCurrentCheckinAttempt(qc, state.pending))) return null;
  // A different mode is a deliberate office/remote switch, not a duplicate click.
  if (
    state.completed?.owner === owner &&
    state.completed.epoch === identityEpoch &&
    state.completed.mode === mode &&
    Date.now() < state.completed.until
  ) return null;

  const attempt = { owner, epoch: identityEpoch, mode };
  state.pending = attempt;
  return attempt;
}

export function checkinSessionEpoch(): number {
  observeIdentity();
  return identityEpoch;
}

export function isSameCheckinSession(attempt: CheckinAttempt): boolean {
  observeIdentity();
  return attempt.epoch === identityEpoch && attempt.owner === useAuthV2.getState().casdoorSub;
}

export function isCurrentCheckinAttempt(qc: QueryClient, attempt: CheckinAttempt): boolean {
  return attempts.get(qc)?.pending === attempt && isSameCheckinSession(attempt);
}

export function finishCheckinAttempt(qc: QueryClient, attempt: CheckinAttempt, accepted = false): void {
  if (!isCurrentCheckinAttempt(qc, attempt)) return;
  const state = attempts.get(qc)!;
  state.pending = null;
  if (accepted) state.completed = { ...attempt, until: Date.now() + 1000 };
}

export function beginCheckinCheckout(qc: QueryClient): boolean {
  const state = attemptState(qc);
  if (state.checkingOut || (state.pending && isCurrentCheckinAttempt(qc, state.pending))) return false;
  state.checkingOut = true;
  return true;
}

export function finishCheckinCheckout(qc: QueryClient, succeeded: boolean): void {
  const state = attempts.get(qc);
  if (!state) return;
  state.checkingOut = false;
  if (succeeded) state.completed = null;
}

export function checkinActionPending(qc: QueryClient): boolean {
  const state = attempts.get(qc);
  return !!state && (state.checkingOut || !!(state.pending && isCurrentCheckinAttempt(qc, state.pending)));
}
